import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TFunction } from 'i18next';
import { FaQuestionCircle } from 'react-icons/fa';
import { NS } from '../../../../U3141';

export type U3141GraphLotDialogHelpMode = 'add' | 'edit';
/** Étape du dialogue ajout : identification du numéro vs détails avant enregistrement. */
export type U3141GraphLotDialogHelpAddPhase = 'identify' | 'details';

function U3141GraphLotDialogHelpContent({
  mode,
  addPhase,
  t,
  contextualHint,
}: {
  mode: U3141GraphLotDialogHelpMode;
  addPhase: U3141GraphLotDialogHelpAddPhase;
  t: TFunction;
  contextualHint: string | null;
}) {
  if (mode === 'edit') {
    return (
      <div className="space-y-2.5 text-sm leading-relaxed text-gray-700">
        <p className="font-medium text-gray-900">{t('dlgEditLotFromGraphHelpHint', { ns: NS })}</p>
        <ul className="list-disc space-y-1.5 pl-5 text-gray-600">
          <li>{t('dlgEditLotFromGraphHelpLotReadonly', { ns: NS })}</li>
          <li>{t('dlgEditLotFromGraphHelpLock', { ns: NS })}</li>
          <li>{t('dlgEditLotFromGraphHelpConcordance', { ns: NS })}</li>
        </ul>
        {contextualHint ? (
          <p className="rounded-md border border-amber-200 bg-amber-50/80 px-2 py-1.5 text-xs text-amber-950">{contextualHint}</p>
        ) : null}
      </div>
    );
  }

  if (addPhase === 'details') {
    return (
      <div className="space-y-2.5 text-sm leading-relaxed text-gray-700">
        <p className="font-medium text-gray-900">{t('dlgAddLotFromGraphHelpStep2Intro', { ns: NS })}</p>
        <ul className="list-disc space-y-1.5 pl-5 text-gray-600">
          <li>{t('dlgAddLotFromGraphHelpStep2ChangeLot', { ns: NS })}</li>
          <li>{t('dlgAddLotFromGraphHelpStep2Lock', { ns: NS })}</li>
          <li>{t('dlgAddLotFromGraphHelpStep2Save', { ns: NS })}</li>
        </ul>
        {contextualHint ? (
          <p className="rounded-md border border-amber-200 bg-amber-50/80 px-2 py-1.5 text-xs text-amber-950">{contextualHint}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2.5 text-sm leading-relaxed text-gray-700">
      <p className="font-medium text-gray-900">{t('dlgAddLotFromGraphHelpStep1Intro', { ns: NS })}</p>
      <p>{t('dlgAddLotFromGraphLotStepHint', { ns: NS })}</p>
      <ul className="list-disc space-y-1.5 pl-5 text-gray-600">
        <li>{t('dlgAddLotFromGraphHelpExisting', { ns: NS })}</li>
        <li>{t('dlgAddLotFromGraphHelpNew', { ns: NS })}</li>
      </ul>
      <p className="text-xs text-gray-500">{t('dlgAddLotFromGraphHelpAutocomplete', { ns: NS })}</p>
    </div>
  );
}

const GRAPH_LOT_HELP_POPOVER_Z = 200;

export function U3141GraphLotDialogHelpPopover({
  mode,
  addDetailsStepActive = false,
  t,
  contextualHint,
  disabled,
}: {
  mode: U3141GraphLotDialogHelpMode;
  /** `mode="add"` : true après validation du numéro (écran détails + Enregistrer). */
  addDetailsStepActive?: boolean;
  t: TFunction;
  contextualHint?: string | null;
  disabled?: boolean;
}) {
  const addPhase: U3141GraphLotDialogHelpAddPhase =
    mode === 'add' && addDetailsStepActive ? 'details' : 'identify';
  const hint = contextualHint ?? null;
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const popoverId = mode === 'edit' ? 'u3141-graph-edit-lot-help-popover' : 'u3141-graph-add-lot-help-popover';

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const panelW = panel?.offsetWidth ?? 360;
    const panelH = panel?.offsetHeight ?? 280;
    let top = r.top - panelH - 8;
    if (top < 8) {
      top = r.bottom + 8;
    }
    let left = r.left;
    const maxLeft = window.innerWidth - panelW - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, hint, addPhase, updatePosition]);

  useEffect(() => {
    setOpen(false);
    setPinned(false);
  }, [addPhase]);

  useEffect(() => {
    if (!open || !pinned) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      setPinned(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, pinned]);

  const showPopover = () => {
    if (disabled) return;
    setOpen(true);
  };

  const hidePopover = () => {
    if (pinned) return;
    setOpen(false);
  };

  const togglePinned = () => {
    if (disabled) return;
    setPinned((prev) => {
      const next = !prev;
      setOpen(next);
      return next;
    });
  };

  const ariaLabel =
    mode === 'edit' ? t('ttlEditLotFromGraphHelp', { ns: NS }) : t('ttlAddLotFromGraphHelp', { ns: NS });

  const panel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={popoverId}
            role="tooltip"
            className="fixed w-[min(22rem,calc(100vw-1rem))] max-w-md rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
            style={{
              top: position?.top ?? -9999,
              left: position?.left ?? 8,
              zIndex: GRAPH_LOT_HELP_POPOVER_Z,
              visibility: position ? 'visible' : 'hidden',
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => {
              if (!pinned) setOpen(false);
            }}
          >
            <U3141GraphLotDialogHelpContent mode={mode} addPhase={addPhase} t={t} contextualHint={hint} />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 shadow-xs transition hover:bg-gray-50 hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={popoverId}
        disabled={disabled}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocus={showPopover}
        onBlur={(e) => {
          if (panelRef.current?.contains(e.relatedTarget as Node)) return;
          if (!pinned) setOpen(false);
        }}
        onClick={togglePinned}
      >
        <FaQuestionCircle className="h-5 w-5" aria-hidden />
      </button>
      {panel}
    </>
  );
}
